import type { GuestAddressSelection } from "./components/GuestHomeStylePortal";

type KakaoProviderWindow = {
  daum?: {
    Postcode: new (options: {
      oncomplete: (data: {
        roadAddress: string;
        autoRoadAddress: string;
        jibunAddress: string;
        autoJibunAddress: string;
        zonecode: string;
        address: string;
        bname: string;
      }) => void;
      onclose?: () => void;
    }) => { open(): void };
  };
  kakao?: {
    Postcode?: new (options: {
      oncomplete: (data: {
        roadAddress: string;
        autoRoadAddress: string;
        jibunAddress: string;
        autoJibunAddress: string;
        zonecode: string;
        address: string;
        bname: string;
      }) => void;
      onclose?: () => void;
    }) => { open(): void };
    maps: {
      load(callback: () => void): void;
      LatLng: new (latitude: number, longitude: number) => unknown;
      Map: new (container: HTMLElement, options: {
        center: unknown;
        level: number;
        draggable: boolean;
        scrollwheel: boolean;
      }) => unknown;
      Marker: new (options: {
        map: unknown;
        position: unknown;
        clickable: boolean;
        draggable: boolean;
      }) => unknown;
      services: {
        Status: { OK: string };
        Geocoder: new () => {
          addressSearch(address: string, callback: (result: Array<{ x: string; y: string }>, status: string) => void): void;
        };
      };
    };
  };
};

type GoogleProviderWindow = {
  google?: {
    accounts: {
      id: {
        initialize(options: { client_id: string; callback: (response: { credential?: string }) => void; auto_select: boolean; cancel_on_tap_outside: boolean; itp_support: boolean }): void;
        prompt(callback: (notification: { isNotDisplayed(): boolean; isSkippedMoment(): boolean; getNotDisplayedReason?(): string; getSkippedReason?(): string }) => void): void;
        cancel(): void;
      };
    };
  };
};

const scriptLoads = new Map<string, Promise<void>>();

function loadExternalScript(id: string, src: string): Promise<void> {
  const existing = document.getElementById(id) as HTMLScriptElement | null;
  if (existing?.dataset.loaded === "true") return Promise.resolve();
  const cached = scriptLoads.get(id);
  if (cached) return cached;
  const promise = new Promise<void>((resolve, reject) => {
    const script = existing ?? document.createElement("script");
    script.id = id;
    script.async = true;
    script.src = src;
    script.addEventListener("load", () => { script.dataset.loaded = "true"; resolve(); }, { once: true });
    script.addEventListener("error", () => {
      // A rejected promise must not permanently prevent an explicit retry.
      scriptLoads.delete(id);
      script.remove();
      reject(new Error("외부 인증·주소 서비스를 불러오지 못했습니다."));
    }, { once: true });
    if (!existing) document.head.append(script);
  });
  scriptLoads.set(id, promise);
  return promise;
}

export interface GuestProviderConfig {
  googleClientId?: string;
  kakaoJavascriptKey?: string;
}

export interface StaffKakaoAddressSelection {
  roadAddress: string;
  lotAddress: string;
  postalCode: string;
  addressNeighborhood?: string;
  latitude: number | null;
  longitude: number | null;
}

export function developmentGuestProviderConfig(): GuestProviderConfig {
  if (!import.meta.env.DEV) return {};
  return {
    googleClientId: import.meta.env.VITE_GUEST_GOOGLE_CLIENT_ID?.trim() || undefined,
    kakaoJavascriptKey: import.meta.env.VITE_KAKAO_MAP_APP_KEY?.trim() || undefined,
  };
}

export function kakaoAddressConfigured(appKey?: string): boolean {
  return Boolean(appKey?.trim());
}

export async function mountKakaoStaticMap(
  appKey: string | undefined,
  container: HTMLElement,
  selection: GuestAddressSelection,
): Promise<() => void> {
  if (!kakaoAddressConfigured(appKey)) throw new Error("카카오 지도 키가 설정되지 않았습니다.");
  await loadExternalScript(
    "guest-kakao-map-sdk",
    `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${encodeURIComponent(appKey!.trim())}&autoload=false&libraries=services`,
  );
  const maps = (window as unknown as KakaoProviderWindow).kakao?.maps;
  if (!maps?.Map || !maps.LatLng || !maps.Marker) throw new Error("카카오 지도 SDK를 확인하지 못했습니다.");
  await new Promise<void>((resolve) => maps.load(resolve));
  container.replaceChildren();
  const position = new maps.LatLng(selection.latitude, selection.longitude);
  const map = new maps.Map(container, {
    center: position,
    level: 3,
    draggable: false,
    scrollwheel: false,
  });
  new maps.Marker({ map, position, clickable: false, draggable: false });
  return () => container.replaceChildren();
}

async function selectKakaoPostcode(): Promise<Omit<StaffKakaoAddressSelection, "latitude" | "longitude"> | undefined> {
  await loadExternalScript("guest-kakao-postcode", "https://t1.kakaocdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js");
  const providerWindow = window as unknown as KakaoProviderWindow;
  const Postcode = providerWindow.kakao?.Postcode ?? providerWindow.daum?.Postcode;
  if (!Postcode) throw new Error("카카오 주소 검색을 시작하지 못했습니다. 잠시 후 다시 시도해 주세요.");
  return new Promise<{
    roadAddress: string;
    lotAddress: string;
    postalCode: string;
    addressNeighborhood?: string;
  } | undefined>((resolve) => {
    let completed = false;
    new Postcode({
      oncomplete(data) {
        completed = true;
        const roadAddress = data.roadAddress || data.autoRoadAddress;
        const lotAddress = data.jibunAddress || data.autoJibunAddress;
        resolve({
          roadAddress,
          lotAddress,
          postalCode: data.zonecode,
          ...(data.bname ? { addressNeighborhood: data.bname } : {}),
        });
      },
      onclose() { if (!completed) resolve(undefined); },
    }).open();
  });
}

export async function geocodeKakaoAddress(appKey: string, address: string): Promise<{ latitude: number; longitude: number }> {
  await loadExternalScript(
    "guest-kakao-map-sdk",
    `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${encodeURIComponent(appKey)}&autoload=false&libraries=services`,
  );
  const providerWindow = window as unknown as KakaoProviderWindow;
  if (!providerWindow.kakao?.maps) throw new Error("카카오 지도 SDK를 확인하지 못했습니다.");
  return new Promise<{ latitude: number; longitude: number }>((resolve, reject) => {
    providerWindow.kakao!.maps.load(() => {
      const geocoder = new providerWindow.kakao!.maps.services.Geocoder();
      geocoder.addressSearch(address, (result, status) => {
        const point = result[0];
        if (status !== providerWindow.kakao!.maps.services.Status.OK || !point) {
          reject(new Error("주소의 지도 좌표를 확인하지 못했습니다."));
          return;
        }
        resolve({ latitude: Number(point.y), longitude: Number(point.x) });
      });
    });
  });
}

export async function selectStaffKakaoAddress(appKey?: string): Promise<StaffKakaoAddressSelection | undefined> {
  const address = await selectKakaoPostcode();
  if (!address) return undefined;
  const normalizedKey = appKey?.trim();
  if (!normalizedKey) return { ...address, latitude: null, longitude: null };
  try {
    return {
      ...address,
      ...await geocodeKakaoAddress(normalizedKey, address.roadAddress || address.lotAddress),
    };
  } catch {
    return { ...address, latitude: null, longitude: null };
  }
}

export async function selectKakaoAddress(appKey?: string): Promise<GuestAddressSelection | undefined> {
  const normalizedKey = appKey?.trim();
  if (!normalizedKey) throw new Error("카카오 주소 검색 키가 설정되지 않아 수동 입력으로 진행합니다.");
  const selected = await selectStaffKakaoAddress(normalizedKey);
  if (!selected) return undefined;
  if (selected.latitude == null || selected.longitude == null) {
    throw new Error("주소는 선택했지만 지도 좌표를 확인하지 못했습니다. 수동 입력으로 진행해 주세요.");
  }
  return { ...selected, latitude: selected.latitude, longitude: selected.longitude };
}

export function guestGoogleConfigured(clientId?: string): boolean {
  return Boolean(clientId?.trim());
}

export async function requestGuestGoogleCredential(clientId?: string): Promise<string> {
  clientId = clientId?.trim();
  if (!clientId) throw new Error("고객 Google 로그인이 설정되지 않았습니다.");
  await loadExternalScript("guest-google-identity", "https://accounts.google.com/gsi/client");
  const providerWindow = window as unknown as GoogleProviderWindow;
  const googleId = providerWindow.google?.accounts.id;
  if (!googleId) throw new Error("Google 로그인을 시작하지 못했습니다.");
  return new Promise<string>((resolve, reject) => {
    let settled = false;
    const finish = (credential?: string, error?: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      if (credential) resolve(credential);
      else reject(error ?? new Error("Google 인증을 완료하지 못했습니다."));
    };
    const timeout = window.setTimeout(() => finish(undefined, new Error("Google 인증 시간이 만료되었습니다.")), 45_000);
    googleId.initialize({
      client_id: clientId,
      auto_select: false,
      cancel_on_tap_outside: false,
      itp_support: true,
      callback: (response) => {
        googleId.cancel();
        finish(response.credential);
      },
    });
    googleId.prompt((notification) => {
      if (notification.isNotDisplayed()) finish(undefined, new Error(`Google 로그인 화면을 열지 못했습니다${notification.getNotDisplayedReason ? ` · ${notification.getNotDisplayedReason()}` : ""}.`));
      else if (notification.isSkippedMoment()) finish(undefined, new Error(`Google 로그인이 취소되었습니다${notification.getSkippedReason ? ` · ${notification.getSkippedReason()}` : ""}.`));
    });
  });
}
